export type SupportPlanId = 'monthly' | 'yearly' | 'lifetime';
export type SupportCurrency = 'usd' | 'gbp' | 'eur' | 'cad' | 'aud';

export interface SupportPlan {
  id: SupportPlanId;
  price: string;
  prices: Record<SupportCurrency, string>;
  originalPrices?: Record<SupportCurrency, string>;
  period: 'month' | 'year' | 'lifetime';
}

export interface SupportStatus {
  enabled: boolean;
  mode: 'test' | 'live';
  plans: SupportPlan[];
  lifetime: { taken: number; limit: number };
  portalUrl?: string | null;
}

export async function getSupportStatus(signal?: AbortSignal): Promise<SupportStatus> {
  const response = await fetch('../support.php', { cache: 'no-store', signal });
  if (!response.ok) throw new Error('Could not load supporter options.');
  return response.json() as Promise<SupportStatus>;
}

export async function startSupportCheckout(
  identity: { did: string; handle: string },
  plan: SupportPlanId,
  currency?: SupportCurrency,
): Promise<string> {
  const response = await fetch('../support-checkout.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...identity, plan, ...(currency ? { currency } : {}) }),
  });
  const value = await response.json().catch(() => ({})) as { url?: string; error?: string };
  if (!response.ok || !value.url) {
    throw new Error(value.error || 'Could not start Stripe checkout.');
  }
  return value.url;
}

const euroRegions = new Set([
  'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
]);

export function preferredSupportCurrency(
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages,
): SupportCurrency | undefined {
  for (const language of languages) {
    try {
      const region = new Intl.Locale(language).maximize().region?.toUpperCase();
      if (region === 'GB') return 'gbp';
      if (region === 'CA') return 'cad';
      if (region === 'AU') return 'aud';
      if (region && euroRegions.has(region)) return 'eur';
      if (region === 'US') return 'usd';
    } catch {
      // Ignore malformed browser locales and let Stripe choose at checkout.
    }
  }
  return undefined;
}

export function openSupportDialog(): void {
  window.dispatchEvent(new CustomEvent('skyspace:open-support'));
}
